class BrandProductBrowser extends HTMLElement {
  connectedCallback() {
    if (this.initialized || this.initializing) return;
    this.initializing = true;
    // Cached facets replace the markup before updating browser history. Wait
    // until that synchronous operation finishes before capturing active filters.
    queueMicrotask(() => {
      this.initializing = false;
      if (this.isConnected) this.initialize();
    });
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;
    this.categories = this.dataset.brand === 'hydraflex'
      ? [['sewer', 'Sewer jetting'], ['industrial', 'Industrial cleaning'], ['excavation', 'Hydro excavation'], ['parts', 'Parts'], ['other', 'Other products']]
      : this.dataset.brand === 'usb'
        ? [['cleaning', 'Cleaning nozzles'], ['bottom', 'Bottom cleaners'], ['cutting', 'Root cutters'], ['rotating', 'Rotating / vibrating nozzles'], ['specialty', 'Specialty nozzles'], ['kits', 'HotJet nozzle kits'], ['other', 'Other products']]
        : [['standard', 'Standard nozzles'], ['rotating', 'Rotating nozzles'], ['chain', 'Chain scrapers'], ['milling', 'Milling cutters'], ['camera', 'Camera nozzles'], ['inserts', 'Inserts'], ['accessories', 'Accessories'], ['parts', 'Parts'], ['other', 'Other products']];
    this.rows = new Map();
    this.groups = new Map();
    this.buttons = new Map();
    this.activeCategory = 'all';
    this.allLabel = this.dataset.brand === 'hydraflex' ? 'All applications' : 'All tools';
    this.storageKey = `brand-product-browser:${this.dataset.brand}`;
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (saved === 'all' || this.categories.some(([key]) => key === saved)) this.activeCategory = saved;
    } catch (_) { /* Browsing remains usable when storage is unavailable. */ }
    this.baseURL = new URL(window.location.href);
    this.seenPages = new Set([Number(this.dataset.page)]);
    this.pending = [];
    this.status = this.querySelector('.brand-browser__status');
    this.pagination = this.querySelector('.brand-browser__pagination');
    this.retry = this.querySelector('.brand-browser__retry');
    const navigation = this.querySelector('.brand-browser__nav');
    for (const [key, label] of [['all', this.allLabel], ...this.categories]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.dataset.label = label;
      button.setAttribute('aria-pressed', String(key === this.activeCategory));
      button.addEventListener('click', () => this.selectCategory(key));
      navigation.append(button);
      this.buttons.set(key, button);
      if (key === 'all') continue;
      const group = document.createElement('section');
      group.className = 'brand-browser__group';
      const heading = document.createElement('h2');
      heading.className = 'brand-browser__group-heading h2';
      heading.id = `Brand-${this.dataset.id}-${key}`;
      heading.textContent = label;
      group.setAttribute('aria-labelledby', heading.id);
      group.append(heading);
      this.querySelector('.brand-browser__groups').append(group);
      this.groups.set(key, { element: group, heading, label, count: 0 });
    }
    this.appendRows(this);
    this.enqueueLinks(this);
    this.querySelector('.brand-browser__source').hidden = true;
    this.querySelector('.brand-browser__controls').hidden = false;
    this.retry.addEventListener('click', () => this.loadPages());
    this.selectCategory(this.activeCategory);
    this.loadPages();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  appendRows(root) {
    root.querySelectorAll('.brand-browser__source [data-product-id]').forEach(row => {
      if (this.rows.has(row.dataset.productId)) return;
      const key = this.groups.has(row.dataset.category) ? row.dataset.category : 'other';
      const group = this.groups.get(key);
      group.element.append(row);
      group.count++;
      this.rows.set(row.dataset.productId, row);
    });
    // A visitor can enter on page two or later; keep the chosen Shopify sort order.
    for (const group of this.groups.values()) {
      [...group.element.querySelectorAll('[data-product-id]')]
        .sort((a, b) => Number(a.dataset.productOrder) - Number(b.dataset.productOrder))
        .forEach(row => group.element.append(row));
    }
  }

  pageURL(href) {
    const url = new URL(href, this.baseURL);
    // Pagination may omit a query parameter; preserve the active Shopify filters.
    for (const key of new Set(this.baseURL.searchParams.keys())) {
      if (key !== 'page' && key !== 'section_id' && !url.searchParams.has(key)) {
        for (const value of this.baseURL.searchParams.getAll(key)) url.searchParams.append(key, value);
      }
    }
    if (url.origin !== this.baseURL.origin || url.pathname !== this.baseURL.pathname) throw new Error('Unexpected pagination location');
    url.searchParams.set('section_id', this.dataset.id);
    url.hash = '';
    return url;
  }

  enqueueLinks(root) {
    root.querySelectorAll('[data-browser-page]').forEach(link => {
      const url = this.pageURL(link.getAttribute('href'));
      const page = Number(url.searchParams.get('page') || 1);
      if (!this.seenPages.has(page) && !this.pending.some(item => item.page === page)) {
        this.pending.push({ page, url });
      }
    });
  }

  selectCategory(key) {
    this.activeCategory = key;
    try { sessionStorage.setItem(this.storageKey, key); } catch (_) { /* Optional persistence. */ }
    for (const [category, button] of this.buttons) {
      button.setAttribute('aria-pressed', String(category === key));
      if (this.complete) button.hidden = category !== 'all' && category !== key && this.groups.get(category).count === 0;
    }
    for (const [category, group] of this.groups) group.element.hidden = group.count === 0 || (key !== 'all' && category !== key);
    if (this.complete) this.showCompleteStatus();
  }

  showCompleteStatus() {
    const count = this.activeCategory === 'all' ? this.rows.size : this.groups.get(this.activeCategory).count;
    const noun = this.dataset.brand === 'hydraflex' ? 'application' : 'tool type';
    this.status.textContent = count === 0
      ? `No matching products in this ${noun}. Try ${this.allLabel.toLowerCase()} or another inlet size.`
      : `${count} ${count === 1 ? 'product' : 'products'}${this.activeCategory === 'all' ? ' across all categories' : ` in this ${noun}`}.`;
  }

  async loadPages() {
    if (this.loading) return;
    this.loading = true;
    this.complete = false;
    this.retry.hidden = true;
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Loading the remaining products. Product groups will update as products arrive.';
    try {
      while (this.pending.length) {
        const item = this.pending[0];
        const response = await fetch(item.url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Product page returned ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected) return;
        const browser = html.querySelector('brand-product-browser');
        if (!browser || browser.dataset.brand !== this.dataset.brand || Number(browser.dataset.page) !== item.page) throw new Error('Unexpected product page');
        this.appendRows(browser);
        this.seenPages.add(item.page);
        this.pending.shift();
        this.enqueueLinks(browser);
        this.selectCategory(this.activeCategory);
      }
      if (this.rows.size !== Number(this.dataset.total)) throw new Error('Some products are not loaded');
      this.complete = true;
      this.pagination.hidden = true;
      for (const [key, button] of this.buttons) {
        const count = key === 'all' ? this.rows.size : this.groups.get(key).count;
        button.textContent = `${button.dataset.label} (${count})`;
        button.hidden = key !== 'all' && count === 0 && key !== this.activeCategory;
      }
      for (const group of this.groups.values()) group.heading.textContent = `${group.label} (${group.count})`;
      this.selectCategory(this.activeCategory);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.pagination.hidden = false;
      this.retry.hidden = false;
      this.status.textContent = `${this.rows.size} of ${this.dataset.total} products loaded. Product groups are incomplete. Retry or use the page links below to browse the remaining products.`;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('brand-product-browser')) customElements.define('brand-product-browser', BrandProductBrowser);
